import React, { useState } from 'react';
import { motion } from 'framer-motion';

const LightingConfigurator = () => {
  // GEO Pattern state
  const [geoActiveColor, setGeoActiveColor] = useState('pink');
  
  // ORNAMENT Pattern state
  const [ornamentActiveColor, setOrnamentActiveColor] = useState('magenta');

  // GEO Pattern images - color-based
  const geoImages = {
    pink: '/configurator/bathroom/bathroom_lighting_pink.jpg',
    blue: '/configurator/bathroom/bathroom_lighting_blue.jpg',
    turquoise: '/configurator/bathroom/bathroom_lighting_turquoise.jpg',
    green: '/configurator/bathroom/bathroom_lighting_green.jpg'
  };

  const geoColors = [
    { name: 'Pink', value: 'pink' },
    { name: 'Sky Blue', value: 'blue' },
    { name: 'Turquoise', value: 'turquoise' },
    { name: 'Green', value: 'green' }
  ];

  // ORNAMENT Pattern images and colors
  const ornamentImages = {
    magenta: '/configurator/bell-lanterns/bell_lanterns_magenta.jpg',
    blue: '/configurator/bell-lanterns/bell_lanterns_blue.jpg',
    turquoise: '/configurator/bell-lanterns/bell_lanterns_turquoise.jpg',
    green: '/configurator/bell-lanterns/bell_lanterns_green.jpg'
  };

  const ornamentColors = [
    { name: 'Magenta', value: 'magenta' },
    { name: 'Blue', value: 'blue' },
    { name: 'Turquoise', value: 'turquoise' },
    { name: 'Green', value: 'green' }
  ];

  return (
    <div className="min-h-screen bg-[hsl(40,20%,97%)] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Title */}
        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-light text-center mb-4 text-gray-900"
        >
          Luxury LED Lantern Collection
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-center text-gray-600 mb-16 max-w-2xl mx-auto"
        >
          Discover our exquisite LED lantern collection. Select your preferred pattern and color to visualize the perfect ambiance.
        </motion.p>

        {/* GEO Pattern Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-light mb-8 text-gray-900">
            GEO Pattern — Indoor Collection
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Image Container */}
            <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 shadow-xl">
              <motion.img
                key={geoActiveColor}
                src={geoImages[geoActiveColor]}
                alt={`GEO ${geoActiveColor}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Controls and Pricing */}
            <div>
              {/* Color Selector */}
              <div className="mb-8">
                <h3 className="text-xl font-medium mb-4 text-gray-900">Select Color</h3>
                <div className="grid grid-cols-2 gap-3">
                  {geoColors.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setGeoActiveColor(color.value)}
                      className={`px-6 py-3 rounded-lg font-medium transition-all ${
                        geoActiveColor === color.value
                          ? 'bg-[hsl(45,100%,70%)] text-gray-900 shadow-lg'
                          : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
                      }`}
                    >
                      {color.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pricing */}
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <h3 className="text-xl font-medium mb-4 text-gray-900">Pricing</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">Size XS</span>
                    <span className="text-lg font-semibold text-gray-900">CHF 440.00</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">Size S</span>
                    <span className="text-lg font-semibold text-gray-900">CHF 540.00</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">Size M</span>
                    <span className="text-lg font-semibold text-gray-900">CHF 690.00</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-700">Size L</span>
                    <span className="text-lg font-semibold text-gray-900">CHF 840.00</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ORNAMENT Pattern Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h2 className="text-3xl font-light mb-8 text-gray-900">
            ORNAMENT Pattern — Outdoor Collection
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Image Container */}
            <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 shadow-xl">
              <motion.img
                key={ornamentActiveColor}
                src={ornamentImages[ornamentActiveColor]}
                alt={`ORNAMENT ${ornamentActiveColor}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Controls and Pricing */}
            <div>
              {/* Color Selector */}
              <div className="mb-8">
                <h3 className="text-xl font-medium mb-4 text-gray-900">Select Color</h3>
                <div className="grid grid-cols-2 gap-3">
                  {ornamentColors.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setOrnamentActiveColor(color.value)}
                      className={`px-6 py-3 rounded-lg font-medium transition-all ${
                        ornamentActiveColor === color.value
                          ? 'bg-[hsl(45,100%,70%)] text-gray-900 shadow-lg'
                          : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
                      }`}
                    >
                      {color.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pricing */}
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <h3 className="text-xl font-medium mb-4 text-gray-900">Pricing</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">Size XS</span>
                    <span className="text-lg font-semibold text-gray-900">CHF 440.00</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">Size S</span>
                    <span className="text-lg font-semibold text-gray-900">CHF 540.00</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">Size M</span>
                    <span className="text-lg font-semibold text-gray-900">CHF 690.00</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-700">Size L</span>
                    <span className="text-lg font-semibold text-gray-900">CHF 840.00</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LightingConfigurator;
